import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fmtDuration, parseClock, PUBLISH_STATUS_LABEL, RECORDING_STATUS_TONE } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { fmtTime } from '../lib/format';
import { hasRole, useMe } from '../lib/session';
import { fmtBytes, type LibraryRecording, type Playback, type RecordingList, type RecordingView } from '../lib/streaming';
import { Button, Card, CardHeader, ErrorText, Field, Input, Pill } from './ui';

/** Plays a recording through a short-lived signed link (ADR-0003); a trim is applied as a media fragment, not a re-encode. */
export function RecordingPlayer({ id, label = 'Play the recording' }: { id: string; label?: string }) {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const play = useMutation({ mutationFn: () => api<Playback>(`/recordings/${id}/playback`) });
  if (!play.data)
    return (
      <div className="flex flex-col gap-2">
        <div>
          <Button variant="primary" onClick={() => play.mutate()} disabled={play.isPending}>
            {play.isPending ? 'Getting a link…' : `▶ ${label}`}
          </Button>
        </div>
        <ErrorText error={play.error} />
      </div>
    );
  const p = play.data;
  return (
    <div className="flex flex-col gap-2">
      <video controls preload="metadata" playsInline className="aspect-video w-full rounded-lg bg-black" src={`${p.url}${p.fragment}`} data-testid="recording-video" />
      <span className="text-xs text-muted">
        Private link, valid until {fmtTime(p.expiresAt, tz)}
        {p.fragment ? ` · plays ${fmtDuration(p.trimStartSeconds ?? 0)}–${p.trimEndSeconds !== null ? fmtDuration(p.trimEndSeconds) : 'end'}` : ''}. Open the page again for a new link.
      </span>
    </div>
  );
}

function TrimForm({ r, onDone }: { r: RecordingView; onDone: () => void }) {
  const [start, setStart] = useState(r.trimStartSeconds !== null ? fmtDuration(r.trimStartSeconds) : '');
  const [end, setEnd] = useState(r.trimEndSeconds !== null ? fmtDuration(r.trimEndSeconds) : '');
  const save = useMutation({
    mutationFn: () => {
      const s = parseClock(start);
      const e = parseClock(end);
      if (Number.isNaN(s) || Number.isNaN(e)) throw new Error('Use minutes:seconds, e.g. 1:30 or 1:02:05.');
      return api(`/recordings/${r.id}/trim`, { method: 'PUT', body: { trimStartSeconds: s, trimEndSeconds: e } });
    },
    onSuccess: onDone,
  });
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(ev) => {
        ev.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label="Start at (m:ss)"><Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="0:00" inputMode="numeric" /></Field>
        <Field label={`End at (of ${fmtDuration(r.durationSeconds)})`}><Input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="end" inputMode="numeric" /></Field>
      </div>
      <span className="text-xs text-muted">The video isn’t cut: viewers’ players start and stop at these times.</span>
      <div><Button type="submit" disabled={save.isPending}>Save trim</Button></div>
      <ErrorText error={save.error} />
    </form>
  );
}

/** One recording for the host: status, size, playback, trim and library state. */
export function RecordingRow({ r, onChange }: { r: RecordingView; onChange: () => void }) {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [open, setOpen] = useState(false);
  const lib = useMutation({ mutationFn: (action: 'PUBLISH' | 'UNPUBLISH') => api(`/recordings/${r.id}/library`, { body: { action } }), onSuccess: onChange });
  const inLibrary = r.libraryItem?.status === 'PUBLISHED';
  // INT-REC-04: room recordings (or a webinar recording without a package) go into the YouTube approval flow
  const navigate = useNavigate();
  const submit = useMutation({ mutationFn: () => api<{ id: string }>(`/recordings/${r.id}/publish-package`, { body: {} }), onSuccess: (p) => void navigate(`/publishing/${p.id}`) });
  return (
    <li className="flex flex-col gap-2 border-b border-line px-4 py-3 last:border-b-0" data-testid="recording-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2 text-sm">
          <Pill tone={RECORDING_STATUS_TONE[r.status]}>{r.status === 'RECORDING' ? '● Recording' : r.statusLabel}</Pill>
          <span className="text-sub">
            {fmtTime(r.startedAt, tz, true)}
            {r.durationSeconds !== null ? ` · ${fmtDuration(r.durationSeconds)}` : ''}
            {r.sizeBytes !== null ? ` · ${fmtBytes(r.sizeBytes)}` : ''}
          </span>
          {r.libraryItem && <Link to={`/library/items/${r.libraryItem.id}`}>{inLibrary ? 'In the library' : 'Library draft (hidden)'}</Link>}
          {r.publishPackage && (
            <Link to={`/publishing/${r.publishPackage.id}`} data-testid="publish-package-link">
              YouTube: {PUBLISH_STATUS_LABEL[r.publishPackage.status].toLowerCase()}
            </Link>
          )}
        </span>
        {r.status === 'READY' && r.canManage && (
          <span className="flex flex-wrap gap-2">
            <Button onClick={() => setOpen((v) => !v)}>{open ? 'Close' : 'Play / trim'}</Button>
            {inLibrary ? <Button variant="ghost" onClick={() => lib.mutate('UNPUBLISH')} disabled={lib.isPending}>Unpublish from library</Button> : <Button onClick={() => lib.mutate('PUBLISH')} disabled={lib.isPending}>{r.libraryItem ? 'Publish to library' : 'Add to library'}</Button>}
            {!r.publishPackage && <Button onClick={() => submit.mutate()} disabled={submit.isPending}>Submit for YouTube</Button>}
          </span>
        )}
      </div>
      {r.error && <p className="m-0 text-sm text-down">{r.error}</p>}
      <ErrorText error={lib.error ?? submit.error} />
      {open && (
        <div className="flex flex-col gap-3">
          <RecordingPlayer id={r.id} />
          <TrimForm r={r} onDone={onChange} />
        </div>
      )}
    </li>
  );
}

/** Recordings of a webinar (host view) or a room (moderators, with start/stop). */
export function RecordingsCard({ webinarId, roomId, live }: { webinarId?: string; roomId?: string; live?: boolean }) {
  const qc = useQueryClient();
  const path = webinarId ? `/webinars/${webinarId}/recordings` : `/rooms/${roomId}/recordings`;
  const key = ['recordings', webinarId ?? roomId];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api<RecordingList>(path), refetchInterval: live ? 5_000 : 30_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const act = useMutation({ mutationFn: (action: 'START' | 'STOP') => api(`/rooms/${roomId}/recording`, { body: { action } }), onSuccess: refresh });
  if (error) return <Card className="p-4 text-sm text-down">{errorText(error)}</Card>;
  if (!data) return null;
  return (
    <Card>
      <CardHeader
        title="Recording"
        right={
          roomId && data.configured ? (
            data.recording ? (
              <Button variant="danger" onClick={() => act.mutate('STOP')} disabled={act.isPending}>■ Stop recording</Button>
            ) : (
              <Button onClick={() => act.mutate('START')} disabled={act.isPending}>● Record this room</Button>
            )
          ) : undefined
        }
      />
      {!data.configured && <p role="status" className="m-0 mx-4 mt-3 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">{data.message}</p>}
      {webinarId && data.configured && <p className="m-0 px-4 pt-3 text-sm text-sub">Webinars record automatically from “Go live” to “End”. Ready recordings join the library for the webinar’s audience.</p>}
      {roomId && data.configured && <p className="m-0 px-4 pt-3 text-sm text-sub">Recordings stay private to the room’s moderators until you add one to the library.</p>}
      <ErrorText error={act.error} />
      <ul className="m-0 flex list-none flex-col p-0 pt-1">
        {!data.recordings.length && <li className="px-4 py-3 text-sm text-muted">No recordings yet.</li>}
        {data.recordings.map((r) => <RecordingRow key={r.id} r={r} onChange={refresh} />)}
      </ul>
    </Card>
  );
}

/** A RECORDING library item: the player for everyone it is shared with; trim and unpublish for its host (INT-WEB-05). */
export function LibraryRecordingPanel({ rec, onChange }: { rec: LibraryRecording; onChange: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const manager = useQuery({ queryKey: ['recording', rec.id], queryFn: () => api<RecordingView>(`/recordings/${rec.id}`), enabled: !!me.data && hasRole(me.data.roles, 'MASTER_TRADER', 'ADMIN', 'RISK_MANAGER') });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['recording', rec.id] });
    onChange();
  };
  return (
    <Card className="flex flex-col gap-3 p-4">
      {rec.status === 'READY' ? <RecordingPlayer id={rec.id} /> : <p className="m-0 text-sub">This recording is {rec.status.toLowerCase()}.</p>}
      <span className="text-sm text-sub">
        Recorded {fmtTime(rec.recordedAt, me.data?.timezone ?? 'UTC', true)}
        {rec.durationSeconds !== null ? ` · ${fmtDuration(rec.durationSeconds)}` : ''}
        {rec.webinarId ? <> · <Link to={`/webinars/${rec.webinarId}`}>the webinar</Link></> : null}
      </span>
      {(rec.links.youtube || rec.links.facebook) && (
        <span className="flex flex-wrap gap-3 text-sm">
          {rec.links.youtube && <a href={rec.links.youtube} target="_blank" rel="noreferrer">Watch on YouTube</a>}
          {rec.links.facebook && <a href={rec.links.facebook} target="_blank" rel="noreferrer">Watch on Facebook</a>}
        </span>
      )}
      {rec.materials.length > 0 && (
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted">Materials from the webinar</span>
          {rec.materials.map((m) => <Link key={m.id} to={`/library/items/${m.id}`}>{m.title}</Link>)}
        </div>
      )}
      {manager.data?.canManage && (
        <ul className="m-0 list-none rounded-lg border border-line p-0">
          <RecordingRow r={manager.data} onChange={refresh} />
        </ul>
      )}
    </Card>
  );
}
